import { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import {
  ReceiptHeader,
  RestaurantInfo,
  OrderInfo,
  MenuItemsList,
  TotalSection,
  ReceiptActions,
  receiptTranslations,
} from './receipt';
import type { OrderData, ReceiptPageProps } from './receipt/types';
import { useOrder } from '@/hooks/orders/useOrder';
import { useStoreDetail } from '@/hooks/useStoreDetail';
import { useLanguage } from '@/contexts/AppContext';
import { checkoutTranslations } from '@/i18n';
import type { Order } from '@/lib/api/types';
import { ApiClientError } from '@/lib/api';
import { derivePaymentMethod } from '@/utils/receipt/derivePaymentMethod';
import { GuestPointsIncentive } from '@/components/points/GuestPointsIncentive';
import { useAuth } from '@/contexts/AuthContext';

// OrderData 및 ReceiptPageProps 타입을 외부에서도 사용할 수 있도록 재익스포트
export type { OrderData, ReceiptPageProps };

const NOTICE_DISMISS_MS = 5000;

type NoticeType = 'postpay' | 'prepay_manual';

function parseNotice(value: string | null): NoticeType | null {
  return value === 'postpay' || value === 'prepay_manual' ? value : null;
}

/**
 * Order(API wire) → OrderData(영수증 표시) 변환.
 * #475 P1-1: deep-link / 새로고침 시 props 부재 → API 재요청 fallback.
 */
function convertOrderToReceiptData(order: Order): OrderData {
  return {
    // #632 F-18: 목록과 동일한 서버 주문번호(ORD-...) 우선 — 내부 id 와 화면 간 불일치 차단.
    orderId: order.order_number || String(order.id),
    timestamp: new Date(order.created_at),
    restaurant: order.store_name,
    tableNumber: order.table_number ?? undefined,
    items: (order.items ?? []).map((item) => ({
      id: String(item.id),
      menuItemId: String(item.menu_id),
      restaurantId: String(order.store_id),
      restaurantName: order.store_name,
      name: item.menu_name,
      description: '',
      category: '',
      quantity: item.quantity,
      price: item.unit_price,
      customizations: [],
    })),
    grossSubtotal: order.gross_subtotal,
    netSubtotal: order.net_subtotal,
    tax: order.tax,
    tip: order.tip ?? 0,
    total: order.total,
    taxRate: order.tax_rate != null ? order.tax_rate / 100 : undefined,
    currency: order.currency,
    // #632 F-19: 'Card' 하드코딩 제거 — 결제 확정 시 combination_code 에서 도출,
    // 미확정(후불 미결제 등)이면 '' 로 결제수단 행 미표시.
    paymentMethod: derivePaymentMethod(order.latest_payment),
    membershipRewardPreview: order.membership_reward_preview,
  };
}

interface ErrorViewProps {
  message: string;
  onBack: () => void;
}

function ErrorView({ message, onBack }: ErrorViewProps) {
  // #522: 15 locale 전수 lookup — Locale 단일 출처 정합.
  const { locale } = useLanguage();
  const t = receiptTranslations[locale];
  return (
    <div className="min-h-screen bg-background">
      <ReceiptHeader onBack={onBack} />
      <div
        role="alert"
        data-testid="receipt-error"
        className="min-h-screen pt-[66px] pb-4 px-4 flex flex-col items-center justify-center gap-4"
      >
        <p className="text-base text-foreground text-center">{message}</p>
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 rounded bg-primary text-primary-foreground"
        >
          {t.close}
        </button>
      </div>
    </div>
  );
}

export function ReceiptPage({
  orderData,
  onBack,
}: ReceiptPageProps) {
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  // #522: 15 locale 전수 lookup — Locale 단일 출처 정합.
  const { locale } = useLanguage();
  const t = receiptTranslations[locale];
  const tCheckout = checkoutTranslations[locale];

  // P1-1 (#475 R1): :orderId param 읽기 + props 부재 시 API 재요청
  const { orderId: routeOrderId } = useParams<{ orderId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

  // props 의 orderData 가 의미 있는지 (orderId 또는 items 가 있는지) 판단.
  // CheckoutPage 가 결제 직후 navigate 하면 orderData 가 채워져 있고, deep-link
  // / 새로고침 시에는 빈 객체 fallback 만 옴 → API 재요청.
  const hasInlineOrderData = Boolean(
    orderData && (orderData.orderId || (orderData.items && orderData.items.length > 0))
  );

  const shouldFetch = !hasInlineOrderData && Boolean(routeOrderId);
  const { order, loading, error } = useOrder(shouldFetch ? routeOrderId : undefined);

  // #632: 더미 주소 시정 — 주문 응답에는 매장 주소가 없어 store_id 로 매장 상세 보충 조회.
  // I2 (#632 codex) 권위 정책: 서버 주문 응답 `order.store_id` 가 **단일 권위** —
  // order 가 확보되면 항상 그것을 사용. 인라인 orderData 는 AppRoutes 가 전역 상태를
  // 모든 /receipt/:orderId 에 주입하므로 stale 할 수 있어, order 부재 시에만
  // items[].restaurantId 를 fallback 으로 사용한다.
  // 식별자 부재/조회 실패 시 storeDetail=null → RestaurantInfo 가 주소/전화 줄 숨김 (더미 금지).
  const storeParam = useMemo(() => {
    if (order?.store_id != null) {
      return String(order.store_id);
    }
    const inlineId = orderData?.items?.find((item) => item.restaurantId?.trim())?.restaurantId;
    return inlineId?.trim() || undefined;
  }, [order, orderData]);
  const { store: storeDetail } = useStoreDetail(storeParam);

  // P1-2 (#475 R1): notice 쿼리 파싱 + 5초 후 query 자동 제거 (새로고침 시 재표시 방지)
  const notice = useMemo(() => parseNotice(searchParams.get('notice')), [searchParams]);
  const noticeMessage = useMemo(() => {
    if (notice === 'postpay') return tCheckout.postpayNotice;
    if (notice === 'prepay_manual') return tCheckout.prepayManualNotice;
    return null;
  }, [notice, tCheckout]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('notice');
          return next;
        },
        { replace: true }
      );
    }, NOTICE_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [notice, setSearchParams]);

  const toggleItem = useCallback((index: number) => {
    setExpandedItems((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  }, []);

  // P1-1: 권한/존재 검증 분기 — 401 / 403 / 404 명시적 에러 노출 (silent fallback 차단)
  if (shouldFetch) {
    if (loading) {
      return (
        <div className="min-h-screen bg-background">
          <ReceiptHeader onBack={onBack} />
          <div
            role="status"
            data-testid="receipt-loading"
            className="min-h-screen pt-[66px] pb-4 px-4 flex items-center justify-center"
          >
            <p className="text-base text-foreground">{t.loadingOrder}</p>
          </div>
        </div>
      );
    }
    if (error) {
      const status = error instanceof ApiClientError ? error.status : undefined;
      let message = t.loadFailed;
      // #475 R3 P1: 권한 실패 시 명시 redirect — onBack(history.back) 만으로는 /receipt/:id 잔류
      let redirectTo: string = ROUTES.HOME;
      if (status === 401) {
        message = t.loginRequired;
        redirectTo = ROUTES.LOGIN;
      } else if (status === 403 || status === 404) {
        message = t.orderNotFound;
        redirectTo = ROUTES.HOME;
      }
      const handleAuthErrorBack = () => {
        // 부모 상태 정리 (paymentFlow 등) 후 명시 redirect
        onBack();
        navigate(redirectTo, { replace: true });
      };
      return <ErrorView message={message} onBack={handleAuthErrorBack} />;
    }
    if (!order) {
      // 로딩 끝 + error 없음 + order null — 비정상. 명시 분기 (silent fallback 금지).
      const handleNotFoundBack = () => {
        onBack();
        navigate(ROUTES.HOME, { replace: true });
      };
      return <ErrorView message={t.orderNotFound} onBack={handleNotFoundBack} />;
    }
  }

  // 표시용 데이터 결정: props 우선 → API 응답 변환 fallback → 빈 객체
  const displayData: OrderData = hasInlineOrderData
    ? orderData
    : order
      ? convertOrderToReceiptData(order)
      : orderData;

  return (
    <div className="min-h-screen bg-background">
      {/* 헤더 */}
      <ReceiptHeader onBack={onBack} />

      {/* 영수증 내용 */}
      <div className="min-h-screen pt-[66px] pb-4 px-4 bg-background">
        <div className="w-full min-h-screen bg-background pb-[140px]">
          {/* P1-2 (#475 R1): 결제 완료 안내 배너 (postpay / prepay_manual) */}
          {noticeMessage && (
            <div
              role="status"
              aria-live="polite"
              data-testid="receipt-notice-banner"
              className="mb-3 rounded-md border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-primary-foreground"
            >
              {noticeMessage}
            </div>
          )}

          {/* 레스토랑 정보 */}
          <RestaurantInfo
            restaurantName={displayData.restaurant}
            address={storeDetail?.address ?? null}
            phone={storeDetail?.phone ?? null}
          />

          {/* 주문 정보 */}
          <OrderInfo orderData={displayData} />

          {/* 메뉴 아이템 목록 */}
          <MenuItemsList
            orderData={displayData}
            expandedItems={expandedItems}
            onToggleItem={toggleItem}
          />

          {/* 합계 섹션 */}
          <TotalSection orderData={displayData} />

          <div className="mt-4 flex justify-center">
            <GuestPointsIncentive
              preview={displayData.membershipRewardPreview}
              isLoggedIn={isLoggedIn}
              locale={locale}
              onJoin={() => {
                navigate(`${ROUTES.LOGIN}?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`);
              }}
            />
          </div>
        </div>

        {/* 하단 고정 버튼 */}
        <ReceiptActions
          orderData={displayData}
          onBack={onBack}
        />
      </div>
    </div>
  );
}
